/**
 * Structured diagnostics for live vs local checkout failures (Sharetribe 409, stale session).
 * Used by CheckoutPage.duck log.error extras and optional console one-liner for support.
 */

const STORAGE_KEY_DEFAULT = 'CheckoutPage';

/**
 * Sharetribe errors may appear on `data.errors` (SDK/transit) or top-level `errors`.
 *
 * @param {Object} e - Error object (may have status, data.errors, message)
 * @returns {Array}
 */
/** API error codes from initiate-privileged / Integration for gift-card speculate failures */
export const GIFT_CARD_CHECKOUT_ERROR_CODES = [
  'gift-card-validation-failed',
  'gift-card-not-found',
];

export const getMarketplaceErrorsArray = e => {
  if (!e || typeof e !== 'object') {
    return [];
  }
  // Normalized `storableError` shape (CheckoutPage.duck) uses `apiErrors`
  if (Array.isArray(e.apiErrors) && e.apiErrors.length > 0) {
    return e.apiErrors;
  }
  const fromData = e.data?.errors;
  if (Array.isArray(fromData) && fromData.length > 0) {
    return fromData;
  }
  if (Array.isArray(e.errors) && e.errors.length > 0) {
    return e.errors;
  }
  const fromResponse = e.response?.data?.errors;
  if (Array.isArray(fromResponse) && fromResponse.length > 0) {
    return fromResponse;
  }
  // Server JSON catch (initiate-privileged) may use `details` instead of `data.errors`
  const details = e.details;
  if (Array.isArray(details) && details.length > 0) {
    return details;
  }
  if (details && typeof details === 'object' && Array.isArray(details.errors) && details.errors.length > 0) {
    return details.errors;
  }
  return [];
};

/**
 * True when a speculate/initiate error should surface in the voucher field (not only as generic checkout noise).
 *
 * @param {Object} payload - error passed through `storableError` or raw fetch error
 */
export const isGiftCardSpeculateError = payload => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  const codeSet = new Set(GIFT_CARD_CHECKOUT_ERROR_CODES);
  const errs = getMarketplaceErrorsArray(payload);
  if (errs.some(err => err?.code && codeSet.has(err.code))) {
    return true;
  }
  if (payload.checkoutDiagnosticCode === 'OUTER_CATCH_GIFT_CARD') {
    return true;
  }
  const message = `${payload.message || ''}`.toLowerCase();
  if (
    message.includes('voucher') ||
    message.includes('gift card') ||
    message.includes('not a valid code')
  ) {
    return true;
  }
  // User applied a voucher: map generic HTTP/Axios failures (e.g. 404) to voucher UI
  if (
    payload.giftCardCodeInRequest &&
    (payload.status === 404 || payload.status === 400 || payload.status === 422)
  ) {
    return true;
  }
  return false;
};

/**
 * Prefer Sharetribe-style `detail` / `title` over raw Axios message for voucher errors.
 *
 * @param {Object} payload
 * @returns {string}
 */
export const getGiftCardValidationErrorDisplayMessage = payload => {
  if (!payload || typeof payload !== 'object') {
    return '';
  }
  const errs = getMarketplaceErrorsArray(payload);
  const first = errs[0];
  if (first) {
    const d = first.detail || first.title;
    if (typeof d === 'string' && d.trim()) {
      return d.trim();
    }
  }
  const m = payload.message || payload.error;
  return typeof m === 'string' ? m : '';
};

/**
 * Serializable summary of a Marketplace / API error (client-side initiate-privileged, SDK).
 *
 * @param {Object} e - Error object (may have status, data.errors, message)
 * @returns {Object|null}
 */
export const formatMarketplaceApiError = e => {
  if (!e || typeof e !== 'object') {
    return null;
  }
  const errorsArray = getMarketplaceErrorsArray(e);
  const errors = errorsArray.length > 0 ? errorsArray : null;
  const normalized =
    Array.isArray(errors) && errors.length > 0
      ? errors.map(err => ({
          status: err.status,
          code: err.code,
          title: err.title,
          detail: err.detail,
          meta: err.meta,
        }))
      : null;

  return {
    httpStatus: e.status,
    statusText: e.statusText,
    message: e.message,
    errors: normalized,
    requestId: e.requestId,
    checkoutDiagnosticCode: e.checkoutDiagnosticCode,
    checkoutDiagnostic: e.checkoutDiagnostic,
  };
};

/**
 * Safe snapshot of checkout sessionStorage for logging (no full listing bodies).
 *
 * @param {string} [storageKey]
 * @returns {Object}
 */
export const snapshotCheckoutSession = (storageKey = STORAGE_KEY_DEFAULT) => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return { available: false };
  }
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) {
      return { available: true, storageKey, hasEntry: false };
    }
    const parsed = JSON.parse(raw);
    const tx = parsed.transaction;
    const listing = parsed.listing;
    return {
      available: true,
      storageKey,
      hasEntry: true,
      storedAt: parsed.storedAt,
      listingId: listing?.id?.uuid,
      transactionId: tx?.id?.uuid,
      lastTransition: tx?.attributes?.lastTransition,
      processName: tx?.attributes?.processName,
    };
  } catch (err) {
    return { available: true, storageKey, parseError: String(err?.message || err) };
  }
};

/**
 * Single console line for copy/paste when debugging production (Sentry off).
 *
 * @param {string} phase - e.g. 'initiate-order' | 'speculate-transaction'
 * @param {Object} apiError - from formatMarketplaceApiError
 * @param {Object} sessionSnapshot - from snapshotCheckoutSession
 */
export const logCheckoutFailureLine = (phase, apiError, sessionSnapshot) => {
  if (typeof console === 'undefined' || !console.warn) {
    return;
  }
  try {
    if (typeof window !== 'undefined') {
      window.__FYK_CHECKOUT_DEBUG__ = window.__FYK_CHECKOUT_DEBUG__ || {};
      window.__FYK_CHECKOUT_DEBUG__.lastFailure = {
        phase,
        at: new Date().toISOString(),
        apiError,
        sessionSnapshot,
      };
      window.__FYK_CHECKOUT_DEBUG__.dump = () => window.__FYK_CHECKOUT_DEBUG__.lastFailure;
    }
    console.warn(
      `[checkout-diagnostics:${phase}]`,
      JSON.stringify({ apiError, sessionSnapshot })
    );
  } catch (e) {
    console.warn('[checkout-diagnostics] failed to stringify', e);
  }
};

/** sessionStorage key for the last purchase failure report (support can paste this). */
export const PURCHASE_FAILURE_REPORT_KEY = 'FYK_PURCHASE_FAILURE_REPORT_V1';

/**
 * One JSON blob a user can paste to support — built from the raw API error at failure time.
 *
 * @param {object} rawError - thrown error (SDK / fetch)
 * @param {object} [context]
 * @param {string} [context.phase] - 'initiate-order' | 'speculate-transaction'
 * @param {string} [context.listingId]
 * @param {object} [context.sessionSnapshot] - from snapshotCheckoutSession before any clear
 */
export const buildPurchaseFailureReport = (rawError, context = {}) => {
  const session =
    context.sessionSnapshot != null
      ? context.sessionSnapshot
      : snapshotCheckoutSession(STORAGE_KEY_DEFAULT);
  const apiErr = formatMarketplaceApiError(rawError);
  return {
    whatThisIs:
      'Automatic error report from Find Your Kind checkout. Paste the whole block to support.',
    capturedAt: new Date().toISOString(),
    pageUrl: typeof window !== 'undefined' ? window.location.href : '',
    phase: context.phase || 'unknown',
    listingId: context.listingId || session?.listingId,
    sessionSnapshot: session,
    apiError: apiErr,
    marketplaceErrors: getMarketplaceErrorsArray(rawError),
    httpStatus: rawError?.status,
    message: rawError?.message,
    requestId: rawError?.requestId || apiErr?.requestId,
    checkoutDiagnosticCode: rawError?.checkoutDiagnosticCode,
    checkoutDiagnostic: rawError?.checkoutDiagnostic,
  };
};

/**
 * Saves report to sessionStorage + window debug — call on every checkout failure before clearing session.
 */
export const savePurchaseFailureReport = (rawError, context = {}) => {
  const report = buildPurchaseFailureReport(rawError, context);
  try {
    if (typeof window !== 'undefined') {
      window.__FYK_CHECKOUT_DEBUG__ = window.__FYK_CHECKOUT_DEBUG__ || {};
      window.__FYK_CHECKOUT_DEBUG__.lastPurchaseFailureReport = report;
      if (window.sessionStorage) {
        window.sessionStorage.setItem(PURCHASE_FAILURE_REPORT_KEY, JSON.stringify(report));
      }
    }
  } catch (err) {
    // non-fatal
  }
  return report;
};

export const clearPurchaseFailureReport = () => {
  try {
    if (typeof window !== 'undefined') {
      if (window.__FYK_CHECKOUT_DEBUG__) {
        delete window.__FYK_CHECKOUT_DEBUG__.lastPurchaseFailureReport;
      }
      if (window.sessionStorage) {
        window.sessionStorage.removeItem(PURCHASE_FAILURE_REPORT_KEY);
      }
    }
  } catch (e) {
    // ignore
  }
};

export const getLastPurchaseFailureReport = () => {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const raw = window.sessionStorage.getItem(PURCHASE_FAILURE_REPORT_KEY);
      if (raw) {
        return JSON.parse(raw);
      }
    }
  } catch (e) {
    // ignore
  }
  if (typeof window !== 'undefined' && window.__FYK_CHECKOUT_DEBUG__?.lastPurchaseFailureReport) {
    return window.__FYK_CHECKOUT_DEBUG__.lastPurchaseFailureReport;
  }
  return null;
};

/**
 * @returns {Promise<boolean>}
 */
export const copyLastPurchaseFailureReportToClipboard = async () => {
  const report = getLastPurchaseFailureReport();
  if (!report) {
    return false;
  }
  const text = JSON.stringify(report, null, 2);
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // ignore
  }
  return false;
};
